// SPDX-License-Identifier: MIT
/**
 * metric-spans (proof, 5–7 beats; reviewed in the Kestrel reel). A headline number decodes on the
 * left while its breakdown races across a scale on the right: one lane per half beat, each with a
 * hot head and a span trail, and a total marker where they add up. At the end the lanes collapse
 * into one bright line that shoots out of frame. Every number shown must be a brand fact.
 *
 * One span is a rate ("97 of 100"): a single heavy lane runs slower across the whole scale and the
 * part it does not reach stays visible as an empty track.
 */
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import * as kit from '../kit/index.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { fitLine, sceneBeats } from './lib.ts';
const LEFT = 190;
const TL = { x: 900, y: 290, w: 860, h: 500 };
export default defineScene({
    id: 'metric-spans',
    version: '1.1.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        value: z.string().check(z.maxLength(8)),
        unit: z.optional(z.string().check(z.maxLength(6))),
        label: z.string().check(z.maxLength(60)),
        spans: z
            .array(z.object({ name: z.string().check(z.maxLength(14)), start: z.number().check(z.gte(0)), length: z.number().check(z.positive()) })).check(z.minLength(1)).check(z.maxLength(8)),
        /** Axis maximum, in the spans' unit. */
        scale: z.number().check(z.positive()),
        /** Unit printed on the axis and next to each span (e.g. "ms", "h", "%"). */
        axisUnit: z._default(z.string().check(z.maxLength(4)), ''),
        /** Axis tick step (default: a quarter of the scale). */
        tick: z.optional(z.number().check(z.positive())),
        /** Mark where the spans end (their total). Ignored for a single span: its head is the total. */
        total: z._default(z.boolean(), true),
    }),
    glyphs: (p) => p.value + (p.unit ?? '') + p.label + p.spans.map((s) => s.name).join('') + p.axisUnit + '0123456789.,',
    motion: () => 0.9,
    audioEvents: (p) => [
        ...[0, 0.12, 0.24, 0.36].map((b) => ({ beat: b, sfx: 'tick' as const, gain: 0.8 })),
        ...p.spans.map((_, i) => ({ beat: 0.5 + i * 0.5, sfx: 'blip' as const, gain: 0.5 })),
    ],
    post: (_p, env) => {
        const L = sceneBeats(env);
        return {
            bloom: env.style.background === 'dark' ? 1.1 : 0.5,
            exposure: 1 + 0.5 * kit.beat.seg(env, L - 0.7, L, kit.curves.launch),
        };
    },
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const L = sceneBeats(env);
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        kit.fx.pool(ctx, TL.x + TL.w / 2, TL.y + TL.h / 2, 900, th.primary, th.dark ? 0.06 : 0.03);
        const collapse = kit.beat.seg(env, L - 1.4, L - 0.6, kit.curves.snap);
        const shoot = kit.beat.seg(env, L - 0.7, L, kit.curves.launch);
        const fmt = (v: number) => `${Number.isInteger(v) ? v : v.toFixed(1)}${props.axisUnit}`;
        // --- The number --------------------------------------------------------------------------
        const numIn = kit.beat.seg(env, 0, 0.5, kit.curves.glide);
        const numOut = kit.beat.seg(env, L - 1.2, L - 0.6, kit.curves.launch);
        const px = fitLine(ctx, env.brand, props.value, 800, 300, 560);
        env.crisp((c) => {
            c.globalAlpha *= numIn * (1 - numOut);
            c.translate(-numOut * 120, 0);
            c.textBaseline = 'alphabetic';
            c.font = font(env.brand, 800, px);
            c.letterSpacing = `${-px / 30}px`;
            c.fillStyle = th.fg;
            c.fillText(kit.text.decode(props.value, kit.beat.seg(env, 0, 0.6), env.t, { seed: 3, charset: '0123456789' }), LEFT - 12, DESIGN_H / 2 + 70);
            const w = c.measureText(props.value).width;
            c.letterSpacing = '0px';
            if (props.unit) {
                c.font = font(env.brand, 700, px * 0.31);
                c.fillStyle = th.accentText;
                c.fillText(props.unit, LEFT + w + 6, DESIGN_H / 2 + 70);
            }
            c.font = font(env.brand, 500, 34);
            c.fillStyle = rgba(th.fg, 0.7);
            const lines = kit.text.wrap(c, props.label, 600);
            lines.slice(0, 2).forEach((l, i) => c.fillText(l.text, LEFT, DESIGN_H / 2 + 150 + i * 44));
        });
        // --- Scale ---------------------------------------------------------------------------------
        const gridIn = kit.beat.seg(env, 0.2, 0.9, kit.curves.glide) * (1 - collapse);
        const toX = (v: number) => TL.x + (v / props.scale) * TL.w;
        const step = props.tick ?? props.scale / 4;
        ctx.font = font(env.brand, 500, 20, 'mono');
        ctx.textAlign = 'center';
        for (let v = 0; v <= props.scale + 1e-9; v += step) {
            const x = toX(v);
            ctx.fillStyle = rgba(th.fg, 0.1 * gridIn);
            ctx.fillRect(x, TL.y, 1, TL.h * gridIn);
            ctx.fillStyle = rgba(th.fg, 0.4 * gridIn);
            ctx.fillText(fmt(v), x, TL.y - 22);
        }
        ctx.textAlign = 'left';
        // --- Lanes ---------------------------------------------------------------------------------
        const single = props.spans.length === 1;
        const laneH = TL.h / props.spans.length;
        const centerY = TL.y + TL.h / 2;
        props.spans.forEach((span, i) => {
            const at = 0.5 + i * 0.5;
            const run = kit.beat.seg(env, at, at + (single ? Math.min(1.6, L - 2.6) : 0.55), kit.curves.glide);
            if (run <= 0)
                return;
            const yLane = TL.y + laneH * (i + 0.5);
            const y = yLane + (centerY - yLane) * collapse;
            const x0 = toX(span.start);
            const x1 = toX(span.start + span.length);
            const head = x0 + (x1 - x0) * run;
            const thick = (single ? 64 : Math.min(18, laneH * 0.3)) * (1 - collapse) + 3 * collapse;
            // The track: for a rate, the share it does not reach has to read as empty, not as ground.
            ctx.fillStyle = rgba(th.fg, (single ? 0.1 : 0.05) * (1 - collapse));
            ctx.fillRect(TL.x, y - thick / 2, TL.w, thick);
            const g = ctx.createLinearGradient(x0, 0, Math.max(x0 + 1, head), 0);
            g.addColorStop(0, rgba(th.primary, 0.25));
            g.addColorStop(1, rgba(th.primary, 0.95));
            ctx.fillStyle = g;
            ctx.fillRect(x0, y - thick / 2, head - x0, thick);
            if (run < 1) {
                ctx.save();
                ctx.globalCompositeOperation = th.light;
                kit.fx.pool(ctx, head, y, 46, th.accent, 0.9);
                ctx.restore();
            }
            ctx.fillStyle = th.hot;
            ctx.fillRect(head - 2, y - thick / 2 - 3, 4, thick + 6);
            const labelA = (1 - collapse) * Math.min(1, run * 2);
            ctx.font = font(env.brand, 500, 24, 'mono');
            ctx.fillStyle = rgba(th.fg, 0.6 * labelA);
            if (single)
                ctx.fillText(span.name, TL.x, y + thick / 2 + 44);
            else {
                ctx.textAlign = 'right';
                ctx.fillText(span.name, TL.x - 24, y + 8);
                ctx.textAlign = 'left';
            }
            if (single)
                ctx.font = font(env.brand, 700, 40, 'mono');
            ctx.fillStyle = rgba(th.accentText, labelA * run);
            // The running value sits after the head, or inside the bar when the head is near the edge.
            const valueText = fmt(Math.round(span.length * run * 10) / 10);
            const vw = ctx.measureText(valueText).width;
            if (single) {
                // Above the head, clear of the lane, kept inside the scale.
                ctx.textAlign = 'center';
                const vx = Math.min(TL.x + TL.w - vw / 2, Math.max(TL.x + vw / 2, head));
                ctx.fillText(valueText, vx, y - thick / 2 - 22);
                ctx.textAlign = 'left';
            }
            else if (head + 14 + vw <= DESIGN_W - 100)
                ctx.fillText(valueText, head + 14, y + 8);
            else {
                ctx.textAlign = 'right';
                ctx.fillText(valueText, head - 14, y - thick / 2 - 12);
                ctx.textAlign = 'left';
            }
        });
        // --- Total ---------------------------------------------------------------------------------
        if (props.total && !single) {
            const total = props.spans.reduce((m, s) => Math.max(m, s.start + s.length), 0);
            const mark = kit.beat.seg(env, 0.5 + props.spans.length * 0.5 + 0.2, 0.5 + props.spans.length * 0.5 + 0.8, kit.curves.glide) *
                (1 - collapse);
            if (mark > 0) {
                const x = toX(total);
                ctx.fillStyle = rgba(th.accentText, mark);
                ctx.fillRect(x - 1.5, TL.y - 8, 3, (TL.h + 16) * mark);
                ctx.font = font(env.brand, 700, 30, 'mono');
                ctx.fillText(fmt(total), x + 14, TL.y + TL.h + 4);
            }
        }
        // --- Collapse and shoot ----------------------------------------------------------------------
        if (shoot > 0) {
            const x = TL.x + TL.w * (1 - shoot) * 0.2 + shoot * DESIGN_W * 1.1;
            ctx.save();
            ctx.globalCompositeOperation = th.light;
            const g = ctx.createLinearGradient(x - 1400, 0, x, 0);
            g.addColorStop(0, rgba(th.primary, 0));
            g.addColorStop(1, th.dark ? rgba('#FFFFFF', 0.95) : rgba(th.primary, 1));
            ctx.fillStyle = g;
            ctx.fillRect(x - 1400, centerY - 3, 1400, 6);
            kit.fx.pool(ctx, x, centerY, 140, th.accent, 1);
            ctx.restore();
        }
    },
});
